import * as Sentry from '@sentry/react';
import mixpanel from 'mixpanel-browser';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { AuthContextProvider } from './contexts/AuthContext';
import { SnackbarContextProvider } from './contexts/SnackbarContext';
import { SystemConfigProvider } from './contexts/SystemConfigContext';
import { UserContextProvider } from './contexts/UserContext';
import { WebsiteUpdaterContextProvider } from './contexts/WebsiteUpdaterContext';
import './index.css';
import './quill.bubble.css';
import './quill.mention.css';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN ?? '',
  integrations: [Sentry.replayIntegration()],
  // Tracing - DISABLED
  tracesSampleRate: 0, // Tracing disabled
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

// import Hotjar from '@hotjar/browser';

// Initiate Hotjar only in prod

// const siteId = 0;
// const hotjarVersion = 6;
// Hotjar.init(siteId, hotjarVersion, {
//   debug: import.meta.env.NODE_ENV === 'development',
// });

import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { Timestamp as FirebaseTimestamp } from 'firebase/firestore';
import { IconoirProvider } from 'iconoir-react';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import { AuditLogContextProvider } from './contexts/AuditLogContext';
import { WhitelabelProvider } from './contexts/WhitelabelContext';

declare module '@superfunnel/types' {
  export type Timestamp = FirebaseTimestamp;
}

mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN ?? '', {
  debug: import.meta.env.NODE_ENV === 'development',
  autocapture: false,
  record_sessions_percent: 0,
  api_host: 'https://api-eu.mixpanel.com',
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
    dehydrate: {
      // Skip persisting queries with meta.persist: false
      // This is needed for queries that return non-serializable data (e.g., Firestore snapshots)
      shouldDehydrateQuery: (query) => query.meta?.persist !== false,
    },
  },
});

const persister = createAsyncStoragePersister({
  storage: window.localStorage,
  key: 'SUPERFUNNEL_QUERY_CACHE',
});

root.render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister }}
    >
      <BrowserRouter>
        <WhitelabelProvider>
          <SnackbarContextProvider>
            <AuthContextProvider>
              <SystemConfigProvider>
                <UserContextProvider>
                  <AnalyticsProvider>
                    <AuditLogContextProvider>
                      <WebsiteUpdaterContextProvider>
                        <IconoirProvider>
                          <App />
                        </IconoirProvider>
                      </WebsiteUpdaterContextProvider>
                    </AuditLogContextProvider>
                  </AnalyticsProvider>
                </UserContextProvider>
              </SystemConfigProvider>
            </AuthContextProvider>
          </SnackbarContextProvider>
        </WhitelabelProvider>
      </BrowserRouter>
    </PersistQueryClientProvider>
  </React.StrictMode>,
);
